import type { AuditDependency } from '../schemas/index';
import {
  dep,
  ManifestParseError,
  type ManifestParser,
  type ParsedManifest,
  type SkippedDependency,
} from './types';

/**
 * Parses a `go.mod` manifest and extracts exact-version Go module dependencies.
 *
 * go.mod semantics
 * ────────────────
 * - Every `require` line is already pinned to an exact version (the `go` tool
 *   manages this).  We do not skip anything on version-format grounds.
 * - `// indirect` entries are included — they are resolved transitive deps and
 *   are equally relevant for vulnerability scanning.
 * - The `v` prefix on versions is preserved (`v1.2.3`) — deps.dev expects it.
 * - Pseudo-versions (`v0.0.0-20210101000000-abcdef123456`) are kept as-is.
 * - `replace` directives:
 *     - Replace pointing to a **local path** (`=> ./path` or `=> ../path`) →
 *       the original entry is moved to `skipped` with reason
 *       `'replaced with local path'`.
 *     - Replace pointing to another **module@version** →
 *       the dependency is recorded with the replacement module name and version
 *       instead of the original.
 */

/** Matches a `replace` directive line (single-line form). */
const REPLACE_LINE_RE = /^replace\s+(\S+)\s+(?:\S+\s+)?=>\s+(\S+)\s+(\S+)/;

/** Matches a replace to a local path (starts with `.` or `/`). */
const LOCAL_PATH_RE = /^\.{0,2}\//;

/** Matches `replace old [oldver] => new [newver]` without a version on the RHS (local path). */
const REPLACE_TO_PATH_RE = /^replace\s+(\S+)\s+(?:\S+\s+)?=>\s+(\.{0,2}\/\S*)/;

/** Matches a single `require path version` line (outside a block). */
const SINGLE_REQUIRE_RE = /^require\s+(\S+)\s+(\S+)/;

/** Parses `name version` from a require block body line (strips `// indirect`). */
const BLOCK_ENTRY_RE = /^(\S+)\s+(\S+)/;

// ---------------------------------------------------------------------------
// Main parser
// ---------------------------------------------------------------------------

/**
 * Parses a `go.mod` file and extracts exact-version Go module dependencies.
 *
 * Throws {@link ManifestParseError} when the content has neither a `module`
 * declaration nor any `require` directives — i.e. is clearly not a `go.mod`.
 */
export const parseGoMod: ManifestParser = (content: string): ParsedManifest => {
  const lines = content.replace(/\r\n/g, '\n').split('\n');

  // -------------------------------------------------------------------------
  // Pass 1: collect replace directives
  // -------------------------------------------------------------------------
  /** Map from original module path → replacement info. */
  type Replacement = { kind: 'local' } | { kind: 'module'; module: string; version: string };

  const replacements = new Map<string, Replacement>();

  let inReplaceBlock = false;

  for (const raw of lines) {
    const line = raw.trim();

    // Enter a replace block
    if (/^replace\s*\(/.test(line)) {
      inReplaceBlock = true;
      continue;
    }
    if (inReplaceBlock) {
      if (line === ')') {
        inReplaceBlock = false;
        continue;
      }
      // Each line: `old [oldver] => new [newver]`
      const blockReplace = /^(\S+)\s+(?:\S+\s+)?=>\s+(\S+)(?:\s+(\S+))?/.exec(line);
      if (blockReplace) {
        const oldMod = blockReplace[1];
        const newTarget = blockReplace[2];
        const newVersion = blockReplace[3];
        if (oldMod === undefined || newTarget === undefined) continue;
        if (LOCAL_PATH_RE.test(newTarget) || (!newVersion && newTarget.startsWith('.'))) {
          replacements.set(oldMod, { kind: 'local' });
        } else if (newVersion) {
          replacements.set(oldMod, { kind: 'module', module: newTarget, version: newVersion });
        } else {
          replacements.set(oldMod, { kind: 'local' });
        }
      }
      continue;
    }

    // Single-line replace
    const pathReplace = REPLACE_TO_PATH_RE.exec(line);
    if (pathReplace) {
      const pathOldMod = pathReplace[1];
      if (pathOldMod !== undefined) replacements.set(pathOldMod, { kind: 'local' });
      continue;
    }
    const modReplace = REPLACE_LINE_RE.exec(line);
    if (modReplace) {
      const oldMod = modReplace[1];
      const newMod = modReplace[2];
      const newVer = modReplace[3];
      if (oldMod === undefined || newMod === undefined || newVer === undefined) continue;
      if (LOCAL_PATH_RE.test(newMod)) {
        replacements.set(oldMod, { kind: 'local' });
      } else {
        replacements.set(oldMod, { kind: 'module', module: newMod, version: newVer });
      }
    }
  }

  // -------------------------------------------------------------------------
  // Pass 2: collect require entries
  // -------------------------------------------------------------------------
  const dependencies: AuditDependency[] = [];
  const skipped: SkippedDependency[] = [];
  const seen = new Set<string>();

  let hasModule = false;
  let hasRequire = false;
  let inRequireBlock = false;

  function emitEntry(modulePath: string, version: string): void {
    if (seen.has(modulePath)) return;
    seen.add(modulePath);

    const replacement = replacements.get(modulePath);
    if (replacement) {
      if (replacement.kind === 'local') {
        skipped.push({
          name: modulePath,
          requirement: version,
          reason: 'replaced with local path',
        });
        return;
      }
      // Use the replacement module + version
      dependencies.push(dep('GO', replacement.module, replacement.version));
      return;
    }

    dependencies.push(dep('GO', modulePath, version));
  }

  for (const raw of lines) {
    const line = raw.trim();
    if (line === '' || line.startsWith('//')) continue;

    if (/^module\s+/.test(line)) {
      hasModule = true;
      continue;
    }

    // Single-line require outside a block
    if (!inRequireBlock) {
      const single = SINGLE_REQUIRE_RE.exec(line);
      if (single) {
        const singleMod = single[1];
        const singleVer = single[2];
        if (singleMod !== undefined && singleVer !== undefined) {
          hasRequire = true;
          emitEntry(singleMod, singleVer);
        }
        continue;
      }
    }

    // Enter require block
    if (/^require\s*\(/.test(line)) {
      inRequireBlock = true;
      hasRequire = true;
      continue;
    }

    if (inRequireBlock) {
      if (line === ')') {
        inRequireBlock = false;
        continue;
      }
      // Strip inline comment before matching
      const noComment = line.replace(/\s*\/\/.*$/, '').trim();
      const entry = BLOCK_ENTRY_RE.exec(noComment);
      if (entry) {
        const entryMod = entry[1];
        const entryVer = entry[2];
        if (entryMod !== undefined && entryVer !== undefined) {
          emitEntry(entryMod, entryVer);
        }
      }
    }
  }

  if (!hasModule && !hasRequire) {
    throw new ManifestParseError(
      'Content does not appear to be a go.mod file (no module or require directives found)',
    );
  }

  return { system: 'GO', dependencies, skipped };
};
